import React, { useEffect, useState, useCallback, useRef } from "react";
import Navbar from "../components/Navbar";
import { Link, useLocation } from "react-router-dom";
import Footer from "../Footer";
import axios from "axios";
import { Bounce, ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";
import Cropper from "react-easy-crop";
import getCroppedImg from "../helper/cropImage";

const ProfileSetting = () => {
  const optionToast = {
    position: "top-right",
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: "colored",
    transition: Bounce,
  };

  const [data, setData] = useState();
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const location = useLocation();
  const [userId, setUserId] = useState();
  const [file, setFile] = useState("");
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const baseUrl =
    window.CARLOCA_UPLOADS + "/users/";

  const [editing, setEditing] = useState(false);

  const [editedData, setEditedData] = useState({
    firstName: data?.data?.first_name || "",
    lastName: data?.data?.last_name || "",
    email: data?.data?.email || "",
    phone: data?.data?.phone || "",
    addressFirst: data?.data?.address_first || "",
    addressSecond: data?.data?.address_second || "",
    city: data?.data?.city || "",
    country: data?.data?.country || "",
    postcode: data?.data?.postcode || "",
    address: data?.data?.address || "",
    cardNo: data?.data?.bank?.card_number || "",
    expiry: data?.data?.bank?.expiry || "",
    cvv: data?.data?.bank?.cvv || "",
  });

  // Google Maps autocomplete refs
  const addressRef = useRef(null);
  const autocompleteRef = useRef(null);
  const inputRef = useRef(null);

  // ---- Load Google Maps Script ----
  useEffect(() => {

    if (window.google && window.google.maps && window.google.maps.places) {
      setMapsLoaded(true);
      initAutocomplete();
      return;
    }

    // Check if script is already being loaded
    if (document.querySelector("#google-maps-script")) {
      // If script is already loading, wait for it to load
      const checkMapsLoaded = () => {
        if (window.google && window.google.maps && window.google.maps.places) {
          setMapsLoaded(true);
          initAutocomplete();
        } else {
          setTimeout(checkMapsLoaded, 100);
        }
      };
      setTimeout(checkMapsLoaded, 100);
      return;
    }

    // Load the Google Maps script
    const script = document.createElement("script");
    script.id = "google-maps-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyAonK15hotzDslX4ePjIbmizRii-7Ng4QE&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setMapsLoaded(true);
      initAutocomplete();
    };
    script.onerror = () => {
      toast.error("Failed to load Google Maps API", optionToast);
      console.error("Google Maps API failed to load");
    };
    document.head.appendChild(script);

    return () => {
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(
          autocompleteRef.current
        );
      }
    };
  }, []);

  // Initialize autocomplete when maps are loaded and component is mounted
  useEffect(() => {
    if (mapsLoaded && inputRef.current) {
      initAutocomplete();
    }
  }, [mapsLoaded, editing]); // Re-initialize when editing mode changes

  const initAutocomplete = () => {
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      console.error("Google Maps API not loaded");
      return;
    }

    if (inputRef.current) {
      // Set options for the autocomplete - restrict to UK addresses
      const options = {
        types: ["geocode"],
        componentRestrictions: { country: "uk" },
      };

      // Clear previous autocomplete instance if exists
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(
          autocompleteRef.current
        );
      }

      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        inputRef.current,
        options
      );

      // Add event listener for when a place is selected
      autocompleteRef.current.addListener("place_changed", handlePlaceSelect);
    }
  };

  const handlePlaceSelect = () => {
    if (!autocompleteRef.current) return;

    const place = autocompleteRef.current.getPlace();
    if (!place.geometry) {
      toast.error("Please select a valid address from the suggestions", optionToast);
      return;
    }

    let city = "",
      country = "",
      postcode = "",
      addressFirst = "",
      addressSecond = "";

    place.address_components.forEach((comp) => {
      if (comp.types.includes("locality") || comp.types.includes("postal_town"))
        city = comp.long_name;
      if (comp.types.includes("country")) country = comp.long_name;
      if (comp.types.includes("postal_code")) postcode = comp.long_name;
      if (comp.types.includes("route")) addressFirst = comp.long_name;
      if (comp.types.includes("administrative_area_level_2"))
        addressSecond = comp.long_name;
    });

    setEditedData((prev) => ({
      ...prev,
      address: place.formatted_address,
      city,
      country,
      postcode,
      addressFirst,
      addressSecond,
    }));
  };

  // ---- Use Current Location ----
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;

        if (window.google) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === "OK" && results[0]) {
              const place = results[0];
              let city = "",
                country = "",
                postcode = "",
                addressFirst = "",
                addressSecond = "";

              place.address_components.forEach((comp) => {
                if (
                  comp.types.includes("locality") ||
                  comp.types.includes("postal_town")
                )
                  city = comp.long_name;
                if (comp.types.includes("country")) country = comp.long_name;
                if (comp.types.includes("postal_code"))
                  postcode = comp.long_name;
                if (comp.types.includes("route")) addressFirst = comp.long_name;
                if (comp.types.includes("administrative_area_level_2"))
                  addressSecond = comp.long_name;
              });

              setEditedData((prev) => ({
                ...prev,
                address: place.formatted_address,
                city,
                country,
                postcode,
                addressFirst,
                addressSecond,
              }));
              toast.success("Location detected", optionToast);
            }
          });
        }
      },
      (err) => {
        toast.error("Unable to fetch location");
        console.error(err);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const handleEditClick = () => setEditing(true);

  const getDataFromlocalStorage = (key) => {
    try {
      const serializedData = localStorage.getItem(key);
      return serializedData ? JSON.parse(serializedData) : null;
    } catch (error) {
      console.error("Error getting data from localStorage:", error);
      return null;
    }
  };

  const storedUserPreferences = getDataFromlocalStorage("userData");

  const handleInputChange = (field, value) => {
    setEditedData((prevData) => ({
      ...prevData,
      [field]: value,
    }));
  };

  const saveDataTolocalStorage = (key, data) => {
    try {
      const serializedData = JSON.stringify(data);
      localStorage.setItem(key, serializedData);
    } catch (error) {
      console.error("Error saving data to localStorage:", error);
    }
  };

  const updateUserDataInLocalStorage = (newData) => {
    try {
      const existingData = JSON.parse(localStorage.getItem("userData")) || {};
      const updated = {
        ...existingData,
        ...newData,
      };
      // Explicitly preserve existing token if no new token is returned
      if (!newData.token && existingData.token) {
        updated.token = existingData.token;
      }
      localStorage.setItem("userData", JSON.stringify(updated));
    } catch (error) {
      console.error("Error updating userData in localStorage:", error);
    }
  };

  const updateProfileDetails = async () => {
    try {
      const response = await axios.post(
        window.CARLOCA_API + "/user/update/profile",
        {
          user_id: storedUserPreferences?.id,
          email: editedData.email,
          phone: editedData.phone,
          address: editedData.address,
          address_first: editedData.addressFirst,
          address_second: editedData.addressSecond,
          addressFirst: editedData.addressFirst,
          addressSecond: editedData.addressSecond,
          city: editedData.city,
          country: editedData.country,
          postcode: editedData.postcode,
          first_name: editedData.firstName,
          last_name: editedData.lastName,
        }
      );

      if (response?.data?.status) {
        toast.success(response?.data?.message, optionToast);
        const mergedData = { ...response?.data?.data };
        if (response?.data?.token) mergedData.token = response?.data?.token;
        updateUserDataInLocalStorage(mergedData);
        fetchData(storedUserPreferences?.id);
      } else {
        toast.error(response?.data?.message, optionToast);
      }
    } catch (error) {
      console.log("An error occurred while fetching data.", error);
    }
  };

  const handleSaveClick = () => {
    setEditing(false);
    updateProfileDetails();
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const userId = searchParams.get("userId");
    setUserId(storedUserPreferences?.id);
    fetchData(userId);
  }, [location.search]);

  const fetchData = async (userId) => {
    try {
      const response = await axios.post(
        window.CARLOCA_API + "/user/get/details",
        {
          user_id: storedUserPreferences?.id,
        }
      );
      setData(response?.data);
      if (response?.data?.status) {
        setEditedData({
          firstName: response?.data?.data?.first_name || "",
          lastName: response?.data?.data?.last_name || "",
          email: response?.data?.data?.email || "",
          phone: response?.data?.data?.phone || "",
          addressFirst: response?.data?.data?.address_first || "",
          addressSecond: response?.data?.data?.address_second || "",
          city: response?.data?.data?.city || "",
          country: response?.data?.data?.country || "",
          postcode: response?.data?.data?.postcode || "",
          address: response?.data.data?.address || "",
          cardNo: response?.data?.data?.bank?.card_number || "",
          expiry: response?.data?.data?.bank?.expiry || "",
          cvv: response?.data?.data?.bank?.cvv || "",
        });
      }
    } catch (error) {
      console.log("An error occurred while fetching data.", error);
    }
  };

  const closeModal = () => {
    document.getElementById("cropModal").classList.remove("show");
    document.getElementById("cropModal").style.display = "none";
  };

  const handleProfile = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result);
        document.getElementById("cropModal").classList.add("show");
        document.getElementById("cropModal").style.display = "block";
      };
      reader.readAsDataURL(file);
    }
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCropImage = async () => {
    try {
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);
      handleProfileData(croppedImage);
      closeModal();
    } catch (error) {
      console.error("Error cropping image:", error);
    }
  };

  const handleProfileData = (selectedFile) => {
    const formData = {
      user_id: storedUserPreferences?.id,
      profile: selectedFile,
    };

    axios
      .post(
        window.CARLOCA_API + "/user/update/profile/picture",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      )
      .then((res) => {
        if (res?.data?.status) {
          toast.success(res?.data?.message, optionToast);
          const mergedPic = { ...(res?.data?.user || res?.data?.data) };
          if (res?.data?.token) mergedPic.token = res?.data?.token;
          updateUserDataInLocalStorage(mergedPic);
          setFile("");
          fetchData(userId);
        } else {
          let errorMessage = res?.data?.message || "Validation failed";
          const errorData = res?.data?.error;
          if (errorData && typeof errorData === 'object' && Object.keys(errorData).length > 0) {
            const firstKey = Object.keys(errorData)[0];
            const firstErrorArr = errorData[firstKey];
            if (Array.isArray(firstErrorArr) && firstErrorArr.length > 0) {
              errorMessage = firstErrorArr[0];
            } else if (typeof firstErrorArr === 'string') {
              errorMessage = firstErrorArr;
            }
          }
          toast.error(errorMessage, optionToast);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  function backto() {
    window.history.back();
  }

  return (
    <>
      <Navbar />

      <div className="ps-page">
        <div className="container px-0">
          <div className="ps-wrapper">

            {/* ── PROFILE HERO CARD ── */}
            <div className="ps-hero-card">
              <button onClick={backto} className="ps-back-btn">
                <i className="fa fa-arrow-left me-1"></i>Back
              </button>
              <div className="ps-avatar-wrap">
                <img
                  className="ps-avatar"
                  alt="Profile"
                  src={
                    data?.data?.profile === undefined || data?.data?.profile === null
                      ? "img/user_default.png"
                      : `${baseUrl}${data?.data?.profile}`
                  }
                />
                <label htmlFor="upload-img" className="ps-avatar-edit" title="Change photo">
                  <i className="fa-solid fa-camera"></i>
                </label>
                <input id="upload-img" onChange={handleProfile} type="file" style={{ display: "none" }} />
              </div>
              <h4 className="ps-name">{data?.data?.first_name} {data?.data?.last_name}</h4>
              <span className="ps-role-badge"><i className="fa-solid fa-circle-check me-1"></i>Member</span>
            </div>

            {/* ── QUICK NAVIGATION ── */}
            <div className="ps-quick-nav">
              <Link to="/mylisting" className="ps-quick-btn">
                <i className="fa-solid fa-car me-2"></i>My Listing
              </Link>
              <Link to="/mycredits" className="ps-quick-btn">
                <i className="fa-solid fa-coins me-2"></i>My Credits
              </Link>
              <Link to="/offers" className="ps-quick-btn">
                <i className="fa-solid fa-handshake me-2"></i>Offers
              </Link>
              <Link to="/fav" className="ps-quick-btn">
                <i className="fa-solid fa-heart me-2"></i>Saved Cars
              </Link>
              <Link to="/buyplan" className="ps-quick-btn">
                <i className="fa-solid fa-star me-2"></i>Buy Credits
              </Link>
            </div>

            {/* ── CONTACT DETAILS CARD ── */}
            <div className="ps-card">
              <div className="ps-card-header">
                <div>
                  <h6 className="ps-card-title">
                    <i className="fa-solid fa-address-card me-2" style={{ color: "#00889c" }}></i>
                    Listing Contact Details
                  </h6>
                  <p className="ps-card-subtitle">Visible to interested buyers when unlocked</p>
                </div>
                <button
                  className={`ps-edit-btn${editing ? " ps-edit-btn-active" : ""}`}
                  onClick={!editing ? handleEditClick : handleSaveClick}
                >
                  {editing
                    ? <><i className="fa-solid fa-check me-1"></i>Save</>
                    : <><i className="fa-solid fa-pen me-1"></i>Edit</>}
                </button>
              </div>

              {editing ? (
                <div className="ps-fields">
                  <div className="row g-3 mb-3">
                    <div className="col-sm-6">
                      <div className="ps-field-group mb-0">
                        <label className="ps-field-label">First Name</label>
                        <input type="text" value={editedData.firstName}
                          onChange={(e) => handleInputChange("firstName", e.target.value)}
                          className="form-control ps-input" placeholder="First Name" />
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <div className="ps-field-group mb-0">
                        <label className="ps-field-label">Last Name</label>
                        <input type="text" value={editedData.lastName}
                          onChange={(e) => handleInputChange("lastName", e.target.value)}
                          className="form-control ps-input" placeholder="Last Name" />
                      </div>
                    </div>
                  </div>
                  <div className="ps-field-group">
                    <label className="ps-field-label">Email</label>
                    <input type="email" value={editedData.email}
                      onChange={(e) => handleInputChange("email", e.target.value)}
                      className="form-control ps-input" placeholder="Email address" />
                  </div>
                  <div className="ps-field-group">
                    <label className="ps-field-label">Phone</label>
                    <input type="tel" value={editedData.phone}
                      onChange={(e) => handleInputChange("phone", e.target.value)}
                      className="form-control ps-input" placeholder="Phone number" />
                  </div>
                  <div className="ps-field-group">
                    <label className="ps-field-label">Address Search</label>
                    <input type="text" ref={inputRef}
                      placeholder="Start typing your address..."
                      className="form-control ps-input mb-2" />
                    <button type="button" onClick={getCurrentLocation} className="ps-location-btn">
                      <i className="fa-solid fa-location-crosshairs me-1"></i>Use My Location
                    </button>
                  </div>
                  <div className="row g-3">
                    <div className="col-sm-6">
                      <div className="ps-field-group mb-0">
                        <label className="ps-field-label">Address Line 1</label>
                        <input type="text" value={editedData.addressFirst}
                          onChange={(e) => handleInputChange("addressFirst", e.target.value)}
                          className="form-control ps-input" placeholder="Street name" />
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <div className="ps-field-group mb-0">
                        <label className="ps-field-label">Address Line 2</label>
                        <input type="text" value={editedData.addressSecond}
                          onChange={(e) => handleInputChange("addressSecond", e.target.value)}
                          className="form-control ps-input" placeholder="County / Area" />
                      </div>
                    </div>
                    <div className="col-sm-4">
                      <div className="ps-field-group mb-0">
                        <label className="ps-field-label">City</label>
                        <input type="text" value={editedData.city}
                          onChange={(e) => handleInputChange("city", e.target.value)}
                          className="form-control ps-input" placeholder="City" />
                      </div>
                    </div>
                    <div className="col-sm-4">
                      <div className="ps-field-group mb-0">
                        <label className="ps-field-label">Country</label>
                        <input type="text" value={editedData.country}
                          onChange={(e) => handleInputChange("country", e.target.value)}
                          className="form-control ps-input" placeholder="Country" />
                      </div>
                    </div>
                    <div className="col-sm-4">
                      <div className="ps-field-group mb-0">
                        <label className="ps-field-label">Post Code</label>
                        <input type="text" value={editedData.postcode}
                          onChange={(e) => handleInputChange("postcode", e.target.value)}
                          className="form-control ps-input" placeholder="SW1A 1AA" />
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="ps-info-grid">
                  <div className="ps-info-item">
                    <span className="ps-info-label"><i className="fa-solid fa-envelope me-1"></i>Email</span>
                    <span className="ps-info-value">{editedData.email || "—"}</span>
                  </div>
                  <div className="ps-info-item">
                    <span className="ps-info-label"><i className="fa-solid fa-phone me-1"></i>Phone</span>
                    <span className="ps-info-value">{editedData.phone || "—"}</span>
                  </div>
                  {editedData.addressFirst && (
                    <div className="ps-info-item">
                      <span className="ps-info-label"><i className="fa-solid fa-location-dot me-1"></i>Address Line 1</span>
                      <span className="ps-info-value">{editedData.addressFirst}</span>
                    </div>
                  )}
                  {editedData.addressSecond && (
                    <div className="ps-info-item">
                      <span className="ps-info-label"><i className="fa-solid fa-location-dot me-1"></i>Address Line 2</span>
                      <span className="ps-info-value">{editedData.addressSecond}</span>
                    </div>
                  )}
                  {editedData.city && (
                    <div className="ps-info-item">
                      <span className="ps-info-label"><i className="fa-solid fa-city me-1"></i>City</span>
                      <span className="ps-info-value">{editedData.city}</span>
                    </div>
                  )}
                  {editedData.country && (
                    <div className="ps-info-item">
                      <span className="ps-info-label"><i className="fa-solid fa-flag me-1"></i>Country</span>
                      <span className="ps-info-value">{editedData.country}</span>
                    </div>
                  )}
                  {editedData.postcode && (
                    <div className="ps-info-item">
                      <span className="ps-info-label"><i className="fa-solid fa-map-pin me-1"></i>Post Code</span>
                      <span className="ps-info-value">{editedData.postcode}</span>
                    </div>
                  )}
                  {!editedData.email && !editedData.phone && (
                    <div className="ps-empty-state">
                      <i className="fa-solid fa-pen-to-square me-2"></i>
                      Tap <strong>Edit</strong> to add your contact details
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ── ACCOUNT DETAILS CARD ── */}
            <div className="ps-card">
              <div className="ps-card-header">
                <div>
                  <h6 className="ps-card-title">
                    <i className="fa-solid fa-circle-info me-2" style={{ color: "#00889c" }}></i>
                    Account Details
                  </h6>
                </div>
              </div>
              <div className="ps-info-grid">
                <div className="ps-info-item">
                  <span className="ps-info-label"><i className="fa-solid fa-calendar me-1"></i>Member Since</span>
                  <span className="ps-info-value">{moment(data?.data?.created_at).format("D MMM YYYY")}</span>
                </div>
                <div className="ps-info-item">
                  <span className="ps-info-label"><i className="fa-solid fa-fingerprint me-1"></i>User ID</span>
                  <span className="ps-info-value">Car-{data?.data?.id}</span>
                </div>
                <div className="ps-info-item">
                  <span className="ps-info-label"><i className="fa-solid fa-circle-check me-1"></i>Status</span>
                  <span className="ps-status-badge">Active</span>
                </div>
              </div>
            </div>

            {/* ── DANGER ZONE ── */}
            <div className="ps-danger-zone">
              <p className="ps-danger-text">Permanently remove your account and all associated data.</p>
              <Link to="/deleteAccount" className="ps-danger-btn">
                <i className="fa-solid fa-trash me-2"></i>Delete My Account
              </Link>
            </div>

          </div>
        </div>
      </div>

      <Footer />

      {/* ── CROP MODAL ── */}
      <div className="modal fade" id="cropModal" tabIndex="-1" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Crop Your Photo</h5>
              <button type="button" className="btn-close" onClick={closeModal}></button>
            </div>
            <div className="modal-body">
              <div style={{ position: "relative", height: "300px", width: "100%" }}>
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={setCrop}
                  onCropComplete={onCropComplete}
                  onZoomChange={setZoom}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary btn-sm" onClick={closeModal}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={handleCropImage}>
                <i className="fa-solid fa-crop me-1"></i>Crop &amp; Save
              </button>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer />
    </>
  );
};

export default ProfileSetting;
